import React, { useEffect, useRef, useState } from "react";
import "./resource.css";
import AddResource from "./addResource";
import errorToast from "../../components/toast/errorToast";
import { useDispatch } from "react-redux";
import { deleteGlossaryAction, listGlossaryAction } from "../../redux/actions/resourceAction";
import moment from "moment";
import { availableLanguages } from "../../components/data/language";
import { GrMoreVertical } from "react-icons/gr";
import AssignToPortal from "./assignToPortal";
import successToast from "../../components/toast/successToast";
import Loading from "../../components/ui/loader/loader";
import Pagination from "../../components/ui/pagination/pagination";
import PageTableLayout from "../../components/ui/PageTableLayout/PageTableLayout";

const Resourse = () => {
  const [show, setShow] = useState(false);
  const [glossaryList, setGlossaryList] = useState([]);
  const [glossaryPage, setGlossaryPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [glossaryPaginationData, setGlossaryPaginationData] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [showClearIndex, setShowClearIndex] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [isAnyCheckboxChecked, setIsAnyCheckboxChecked] = useState(false);
  const [resourceShow, setResourceShow] = useState(false);
  const [selectedResources, setSelectedResources] = useState([]);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const dropdownRef = useRef(null);

  useEffect(() => {
    document.title = "Resources - Acumen Translation Hub";
  }, []);

  useEffect(() => {
    setLoading(true);
    dispatch(
      listGlossaryAction({ page: glossaryPage, page_size: pageSize }, onSuccess, onError)
    );
  }, [glossaryPage, pageSize, dispatch]);

  const onSuccess = (data) => {
    const { glossaries, pagination } = data.data.data;
    setGlossaryList(glossaries);
    setGlossaryPaginationData({
      currentPage: pagination.currentPage,
      total: pagination.total ?? pagination.count ?? glossaries.length,
      totalPages: pagination.totalPages,
      nextPage: pagination.currentPage < pagination.totalPages,
    });
    setLoading(false);
  };

  const onError = (data) => {
    errorToast(data?.data?.message || "Something went wrong");
    setLoading(false);
  };

  const reloadData = () => {
    setLoading(true);
    dispatch(
      listGlossaryAction({ page: glossaryPage, page_size: pageSize }, onSuccess, onError)
    );
  };

  const getLangName = (value) => {
    const match = availableLanguages.find((item) => item.value === value);
    return match ? match.label : value;
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowOptions(false);
      setShowClearIndex(null);
    }
  };

  const handleMoreOptionsClick = (resource, index) => {
    if (showClearIndex === index && showOptions) {
      setShowOptions(false);
      setShowClearIndex(null);
    } else {
      setSelectedResource(resource);
      setShowClearIndex(index);
      setShowOptions(true);
    }
  };

  const handleDelete = (id) => {
    setShowOptions(false);
    setShowClearIndex(null);
    setLoading(true);
    dispatch(deleteGlossaryAction({ glosaryId: id }, onDeleteSuccess, onError));
  };

  const onDeleteSuccess = (data) => {
    successToast(data.data.message);
    reloadData();
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    const updatedList = glossaryList.map((item) => ({ ...item, isChecked }));
    setGlossaryList(updatedList);
    setSelectedResources(isChecked ? updatedList.map((item) => item.id) : []);
    setIsAnyCheckboxChecked(isChecked);
  };

  const handleCheckboxChange = (index) => {
    const updatedList = glossaryList.map((item, i) =>
      i === index ? { ...item, isChecked: !item.isChecked } : item
    );
    setGlossaryList(updatedList);
    const anyChecked = updatedList.some((item) => item.isChecked);
    setIsAnyCheckboxChecked(anyChecked);
    setSelectAll(updatedList.every((item) => item.isChecked));
    setSelectedResources(updatedList.filter((item) => item.isChecked).map((item) => item.id));
  };

  const canAssign = selectAll || isAnyCheckboxChecked;

  return (
    <>
      <PageTableLayout
        title="Resources"
        onClick={handleClickOutside}
        headerActions={
          <div className="d-flex align-items-center gap-2 ms-auto me-3">
            <button
              className="btn btn-sm btn-outline-primary fw_600"
              onClick={() => setShow(true)}
            >
              + ADD RESOURCE
            </button>
            <button
              className="btn btn-sm btn-outline-secondary fw_600"
              onClick={() => setResourceShow(true)}
              disabled={!canAssign}
              title={canAssign ? "Assign selected to portals" : "Select at least one resource first"}
            >
              + ASSIGN TO PORTALS
            </button>
          </div>
        }
        description={
          <div className="text-start">
            <h5 className="fw_600 mb-1">Glossaries</h5>
            <p className="mb-0">
              Glossaries are custom dictionaries that help you consistently translate terms and named entities.{" "}
              <a
                href="https://cloud.google.com/translation-hub/docs/admin-glossary"
                target="_blank"
                rel="noreferrer"
                className="text-primary text-decoration-underline"
              >
                Learn more
              </a>
            </p>
          </div>
        }
        pagination={
          glossaryPaginationData?.total > 0 ? (
            <Pagination
              data={glossaryPaginationData}
              nextFunc={() => setGlossaryPage((p) => p + 1)}
              prevFunc={() => setGlossaryPage((p) => p - 1)}
              setPageRowFunc={(val) => {
                setGlossaryPage(1);
                setPageSize(Number(val));
              }}
            />
          ) : null
        }
      >
        <thead>
          <tr className="text-start text-nowrap">
            <th style={{ width: 40 }}>
              <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
            </th>
            <th>Resource Name</th>
            <th>Language Set</th>
            <th>Portal Names</th>
            <th>Create Time</th>
            <th>Last Updated</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="7">
                <Loading text="Loading resources..." style={{ height: "5rem" }} />
              </td>
            </tr>
          ) : glossaryList.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center text-muted py-5 fs_14">
                No resources found
              </td>
            </tr>
          ) : (
            glossaryList.map((item, index) => (
              <tr key={item.id} className="text-start">
                <td>
                  <input
                    type="checkbox"
                    checked={item?.isChecked || false}
                    onChange={() => handleCheckboxChange(index)}
                  />
                </td>
                <td>{item?.name}</td>
                <td>
                  {item?.language_set
                    ?.map((lang) => `${getLangName(lang)} (${lang})`)
                    .join(", ") || "—"}
                </td>
                <td>
                  {item?.glossaryPortals
                    ?.map((p) => p?.portal?.portal_name)
                    .join(", ") || "—"}
                </td>
                <td className="text-nowrap">
                  {moment(item?.createdAt).format("MM/DD/YY, hh:mm A")}
                </td>
                <td className="text-nowrap">
                  {moment(item?.updatedAt).format("MM/DD/YY, hh:mm A")}
                </td>
                <td className="position-relative">
                  <GrMoreVertical
                    className="cursor_pointer"
                    onClick={() => handleMoreOptionsClick(item, index)}
                  />
                  {showOptions && showClearIndex === index && (
                    <div
                      className="position-absolute end-0 mt-1 card shadow-sm"
                      style={{ zIndex: 100, minWidth: 120 }}
                      ref={dropdownRef}
                    >
                      <div className="card-body p-0">
                        <button
                          className="btn btn-sm text-danger w-100 text-start px-3 py-2 hover-effect border-0"
                          onClick={() => handleDelete(item?.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </PageTableLayout>

      {show && (
        <AddResource show={show} onHide={() => setShow(false)} reloadData={reloadData} />
      )}
      {resourceShow && (
        <AssignToPortal
          show={resourceShow}
          onHide={() => setResourceShow(false)}
          selectedResources={selectedResources}
          reloadData={reloadData}
        />
      )}
    </>
  );
};

export default Resourse;
